import { Typography, Box } from '@mui/material';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import React, { useMemo, useState } from 'react';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { ColumnsData, Table } from '../Tables/Table';
import { DashboardTabWrapper } from '../ScreenContainer/EsDiscoveryContainer/DashboardTabWrapper/DashboardTabWrapper';
import { getUsersDashboard } from '../../redux/user/actions';
import { ROUTES } from '../../types/global/routes.types';
import CalendarIcon from '../CustomSVGIcons/CalendarIcon';
import { StatusIcon } from '../CustomSVGIcons/StatusIcon';
import UserIcon from '../CustomSVGIcons/UserIcon';
import { DescriptionIcon } from '../CustomSVGIcons/DescriptionIcon';
import { TrophyIcon } from '../CustomSVGIcons/TrophyIcon';
import CustomLazyLoadImage from '../Image/CustomLazyLoadImage';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import { StatusBadge } from '../Tables/StatusBadge';
import { EsEmptyContentCard } from '../EsCards/EsEmptyContentCard';
import trophyOutline from '../../asset/images/icons/es_trophy_orange_big.svg';
import trophyFilled from '../../asset/images/icons/es_trophy_orange_small.svg';
import { RichTextViewer } from '../Common/RichTextViewer';
import { MixPanel, MIXPANEL_EVENT } from '../../analytics/Mixpanel';
import { PreviewSubmission } from '../ScreenContainer/EsActiveContestContainer/PreviewSubmission';
import { ISubmission } from '../../types/global/helper';
import { useDispatch } from 'react-redux';
import { rescindSubmission } from '../../redux/contest/actions';
import { TMoreMenuItem } from '../More/more.data';
import { navigate } from '../../redux/navigator/action';
import { replaceRouteParam } from '../../utils/routesProcessor';

interface ISubmissionsTab {
    profile_pic?: string;
}

export const SubmissionsTab: React.FC<ISubmissionsTab> = ({ profile_pic }) => {
    const { themeStyle } = useThemeContext();
    const TAG = ['Active Submissions', 'Past Submissions'];
    const [selectedTag] = useState(TAG[0]);
    const [selectedSubmission, setSelectedSubmission] = useState<ISubmission>();
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();

    const navigateToActiveContests = () => {
        dispatch(
            navigate({
                routes: `${ROUTES.ESDISCOVERY}`,
                skipAuthentication: true,
            }),
        );
    };

    const menu = useMemo(() => {
        const MORE_MENU: TMoreMenuItem[] = [
            {
                id: 'view_project',
                title: 'View Project Details',
            },
            {
                id: 'manage_submission',
                title: 'Manage Submission',
            },
        ];
        if (
            selectedSubmission &&
            selectedSubmission.judging_status === 'submitted' &&
            selectedSubmission.contest.contest_phase !== 'closed'
        ) {
            MORE_MENU.push({
                id: 'rescind_submission',
                title: 'Rescind Submission',
            });
        }
        return MORE_MENU;
    }, [selectedSubmission]);

    console.log('selectedSubmission', selectedSubmission);

    const NoSubmissionsData = () => {
        return (
            <Box
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                }}>
                <EsEmptyContentCard
                    outlineIcon={trophyOutline}
                    outlineIconAlt="trophy icon"
                    filledIcon={trophyFilled}
                    filledIconAlt="add contest icon button"
                    title={`No ${!!selectedTag ? selectedTag.toLowerCase() : 'Selection'}`}
                    description={`You don't have any ${
                        !!selectedTag ? selectedTag.toLowerCase() : 'Submissions'
                    } at the moment.`}
                    onClickHandle={navigateToActiveContests}
                    buttonText="Explore"
                />
            </Box>
        );
    };

    const renderCustomStatus = (params: any) => {
        const contestPhase = params.contest.contest_phase;
        return (
            <StatusBadge
                statusType="custom"
                customStatusType={
                    contestPhase === 'submission'
                        ? 'positive'
                        : contestPhase === 'vote' || contestPhase === 'preparation'
                        ? 'neutral'
                        : 'negative'
                }
                customStatusPositive={'open'}
                customStatusNeutral={contestPhase === 'vote' ? 'voting' : contestPhase}
                customStatusNegative={contestPhase}
            />
        );
    };

    const handleRescind = (submission: ISubmission) => {
        MixPanel.track(MIXPANEL_EVENT.RESCIND_SUBMISSION_CLICK, {
            buttonAction: 'OPEN_RESCIND_SUBMISSION',
            buttonName: 'RESCIND_SUBMISSION',
            submissionId: submission.contest._id,
            submissionName: submission.contest.title,
        });
        dispatch(rescindSubmission(submission));

        setOpen(false);
    };

    const renderCustomImage = (params: any) => {
        return (
            <CustomLazyLoadImage
                src={params.contest.feature_image}
                gradientStyle={{
                    borderRadius: 3,
                }}
                style={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                }}
                noImageStyle={{
                    height: '65px',
                    minWidth: '60px',
                    width: '80px',
                    maxWidth: '100px',
                    border: `0.1px solid white`,
                    borderRadius: '10px',
                    objectFit: 'cover',
                    opacity: 1,
                }}
                alt={params.contest.title}
            />
        );
    };

    const renderCustomTitle = (params: any) => {
        return (
            <Typography
                style={{
                    fontFamily: FONT_ENUM.MAIN_MEDIUM,
                    fontSize: 13.5,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: theme[themeStyle].tag_white,
                }}>
                {params.contest.title}
            </Typography>
        );
    };

    const renderCustomDescription = (params: any) => {
        return (
            <RichTextViewer
                content={params.contest.description}
                enableReadMore={false}
                lineClamp={1}
                characterLimit={35}
            />
        );
    };

    const renderCustomHostTag = (params: any) => {
        return (
            <CustomChipGroup
                tagSx={{
                    px: '7px',
                    padding: 2,
                    borderRadius: 13,
                    fontSize: 13,
                }}
                maxVisible={3}
                withNiceColor
                tags={[params.contest.host.name]}
            />
        );
    };

    const handleOnMenuClicks = (type: string, payload: any) => {
        switch (type) {
            case 'view_project':
                if (payload.project?.video_id) {
                    const routes = replaceRouteParam(ROUTES.PROJECT_DETAILS, 'slug', payload.project.video_id);
                    dispatch(
                        navigate({
                            routes,
                        }),
                    );
                }
                break;
            case 'manage_submission':
                MixPanel.track(MIXPANEL_EVENT.ACTIVE_SUBMISSIONS_CLICK, {
                    buttonAction: 'CLICK_ACTIVE_SUBMISSIONS',
                    buttonName: 'ACTIVE_SUBMISSIONS_CARD ',
                    contestId: payload.contest._id,
                    contestName: payload.contest.title,
                });
                setSelectedSubmission(payload);
                setOpen(true);
                break;
            case 'rescind_submission':
                MixPanel.track(MIXPANEL_EVENT.RESCIND_SUBMISSION_CLICK, {
                    buttonAction: 'OPEN_RESCIND_SUBMISSION',
                    buttonName: 'RESCIND_SUBMISSION',
                    submissionId: payload.contest._id,
                    submissionName: payload.contest.title,
                });
                dispatch(rescindSubmission(payload));
                break;
        }
    };

    const columnsData: ColumnsData[] = [
        {
            field: 'image',
            label: '',
            type: 'custom',
            customCell: renderCustomImage,
            sortable: true,
            width: 100,
        },
        {
            field: 'title',
            label: 'Title',
            type: 'custom',
            customCell: renderCustomTitle,
            sortable: true,
            width: 300,
            headerIcon: <TrophyIcon style={{ color: theme[themeStyle].tableIcon, width: '2em', height: '2em' }} />,
        },
        {
            field: 'description',
            label: 'Description',
            type: 'custom',
            customCell: renderCustomDescription,
            sortable: true,
            width: 300,
            headerIcon: <DescriptionIcon style={{ color: theme[themeStyle].tableIcon, width: '2em', height: '2em' }} />,
        },
        {
            field: 'host',
            label: 'Hosted By',
            type: 'custom',
            customCell: renderCustomHostTag,
            sortable: true,
            alignColumn: 'center',
            width: 250,
            headerIcon: <UserIcon style={{ width: '2em', height: '2em' }} stroke={theme[themeStyle].tableIcon} />,
        },
        {
            field: 'contest_phase',
            label: 'Phase',
            type: 'custom',
            customCell: renderCustomStatus,
            sortable: true,
            width: 150,
            alignColumn: 'center',
            headerIcon: <StatusIcon style={{ color: theme[themeStyle].tableIcon, width: '2em', height: '2em' }} />,
        },
        {
            field: 'createdAt',
            label: 'Date Created',
            type: 'text',
            textType: 'date',
            sortable: true,
            width: 200,
            headerIcon: <CalendarIcon style={{ width: '2em', height: '2em' }} fill={theme[themeStyle].tableIcon} />,
        },
        {
            field: 'actions',
            label: 'Actions',
            type: 'action',
            sortable: false,
            width: 90,
            alignColumn: 'right',
        },
    ];

    return (
        <>
            <DashboardTabWrapper profile_pic={profile_pic || ''} tabText="Here's a list of your recent submissions">
                <Table
                    columns={columnsData}
                    rowsPerPageOptions={[10, 25, 50]}
                    tableType="submissions"
                    rowHeight={70}
                    headerHeight={60}
                    getTableData={getUsersDashboard}
                    dashboardCategory="submissions"
                    textSx={{
                        fontSize: 13.5,
                    }}
                    slots={{ noRowsOverlay: NoSubmissionsData }}
                    fullscreenMoreIcon
                    fullscreenMoreMenu={menu}
                    onActionMenuButtonClicked={payload => {
                        setSelectedSubmission(payload);
                    }}
                    onFullscreenMenuClick={handleOnMenuClicks}
                    sx={{
                        minHeight: 500,
                    }}
                />
            </DashboardTabWrapper>
            <PreviewSubmission
                open={open}
                submission={selectedSubmission}
                onClose={() => setOpen(false)}
                onRescindSubmisison={handleRescind}
            />
        </>
    );
};
